import { useCallback, useEffect, useMemo, useState } from 'react'

import { RouteKey } from '@store/constants'

import { ProjectGroupItem } from '@api/showcase-room'

import { notifyError } from '@utilities/adgroup-utilities/notifier'
import useRoomConfigData from '@utilities/useRoomConfigData'

import { ProjectType } from '@adUtilities/constants'

import parseRouteParams from './utils/routeParamsParser'
import useProjectSwitchUtils from './utils/useProjectSwitchUtils'

interface InitialValues {
  isPanelVisible: boolean
  isProcessing: boolean
}

const INITIAL_VALUES: InitialValues = {
  isPanelVisible: false,
  isProcessing: false,
}

interface UseProjectSwitchArgs {
  initialValues?: Partial<InitialValues>
  resetTriggerKey?: string | number | boolean | object
}

const useProjectSwitch = (args?: UseProjectSwitchArgs) => {
  const { initialValues: initialValuesProp, resetTriggerKey } = args || {}

  const initialValues = useMemo<InitialValues>(
    () => ({
      isPanelVisible:
        initialValuesProp?.isPanelVisible ?? INITIAL_VALUES.isPanelVisible,
      isProcessing:
        initialValuesProp?.isProcessing ?? INITIAL_VALUES.isProcessing,
    }),
    [initialValuesProp]
  )

  const [isPanelVisible, setIsPanelVisible] = useState(
    initialValues.isPanelVisible
  )
  const [isProcessing, setIsProcessing] = useState(initialValues.isProcessing)

  const { roomConfigData } = useRoomConfigData()
  const { projectSwitch, projectGroup = [] } = roomConfigData || {}

  const enable = !!projectSwitch?.enabled

  const { startProjectSwitch, validateRedirectPath } = useProjectSwitchUtils()

  const handleTogglePanel = useCallback(() => {
    setIsPanelVisible((prevVal) => !prevVal)
  }, [])

  const getRedirectRoute = useCallback<
    (project: ProjectGroupItem) => {
      route: string
      urlParams?: string
    }
  >(
    ({ type: projectType, route: routeWithParams }) => {
      const { route = '', urlParams = '' } = routeWithParams
        ? parseRouteParams(routeWithParams, projectType)
        : {}

      if (!route && projectType === ProjectType.Apartment) {
        return {
          route: RouteKey.AREA_VIEW,
        }
      }
      if (!route && projectType === ProjectType.HouseAndLand) {
        return {
          route: RouteKey.AREA_VIEW_HOUSE_AND_LAND,
        }
      }
      if (route && validateRedirectPath(route)) {
        return {
          route,
          urlParams,
        }
      }
      return {
        route: RouteKey.VISION,
      }
    },
    [validateRedirectPath]
  )

  const handleProjectSwitch = useCallback(
    async (projectId: string) => {
      const projectGroupItem = projectGroup.find(
        (project) => project.id === projectId
      )
      if (!projectGroupItem) {
        return
      }

      const { route, urlParams } = getRedirectRoute(projectGroupItem)

      setIsProcessing(true)
      const { status, message } = await startProjectSwitch({
        project: {
          id: projectGroupItem.id,
          uuid: projectGroupItem.uuid,
          name: projectGroupItem.name,
          canonicalName: projectGroupItem.canonicalName,
          label: projectGroupItem.label,
        },
        redirectTo: route,
        redirectUrlParams: urlParams,
      })
      setIsProcessing(false)

      if (!status && message) {
        notifyError(message)
      }
    },
    [projectGroup, getRedirectRoute, startProjectSwitch]
  )

  const reset = useCallback(() => {
    setIsPanelVisible(initialValues.isPanelVisible)
    setIsProcessing(initialValues.isProcessing)
  }, [initialValues])

  useEffect(() => {
    reset()
  }, [resetTriggerKey])

  return {
    enable,
    isPanelVisible,
    isProcessing,
    projectGroup,
    handleTogglePanel,
    handleProjectSwitch,
  }
}

export default useProjectSwitch
